import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdPsychology, MdScience, MdUploadFile, MdCalculate } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import YsaTrainingTab from '../../components/admin/YsaTrainingTab';
import YsaParamsTab from '../../components/admin/YsaParamsTab';
import YsaCalculateTab from '../../components/admin/YsaCalculateTab';

export default function AdminYsaPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [company, setCompany] = useState(null);
    const [config, setConfig] = useState({ categories: [] });
    const [sensors, setSensors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('training');

    const fetchConfig = useCallback(async () => {
        try {
            const res = await api.get(`/ysa/config/${id}`);
            setConfig(res.data.data || { categories: [] });
        } catch {
            toast.error(t('ysa.configLoadFailed'));
        }
    }, [id, t]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, sensorsRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/sensors/company/${id}`)
            ]);
            setCompany(compRes.data.data);
            setSensors(sensorsRes.data.data || []);
            await fetchConfig();
        } catch {
            toast.error(t('ysa.sensorsLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [id, t, fetchConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const tabs = [
        { key: 'training', label: t('ysa.tabTraining'), icon: <MdScience /> },
        { key: 'params', label: t('ysa.tabParams'), icon: <MdUploadFile /> },
        { key: 'calculate', label: t('ysa.tabCalculate'), icon: <MdCalculate /> }
    ];

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }} /></div>;

    return (
        <div className="page-wrapper">
            <div className="page-header" style={{ marginBottom: 24, alignItems: 'center' }}>
                <div className="page-header-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(`/admin/companies/${id}`)} id="back-btn">
                        <MdArrowBack />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(79,142,247,0.12)', color: 'var(--primary)' }}>
                            <MdPsychology size={26} />
                        </div>
                        <div>
                            <h1 className="page-title" style={{ margin: 0, fontSize: 24 }}>{t('ysa.title')}</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        id={`ysa-tab-${tab.key}`}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 18px', border: 'none',
                            borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                            background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: 14,
                            color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)',
                            fontFamily: 'Inter, sans-serif', transition: 'color 0.2s, border-color 0.2s'
                        }}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'training' && (
                <YsaTrainingTab companyId={id} config={config} sensors={sensors} refetchConfig={fetchConfig} />
            )}
            {activeTab === 'params' && (
                <YsaParamsTab companyId={id} />
            )}
            {activeTab === 'calculate' && (
                <YsaCalculateTab companyId={id} config={config} sensors={sensors} />
            )}
        </div>
    );
}
